import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Paper, Stack, Divider, Button, FormControl, FormLabel, Select, MenuItem } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import authService from '../../../api-authorization/AuthorizeService';

const initialStatus = 'Waiting for heartbeats to initialize';
const heartBeatLookBackThreshold = 3;

function normalizeUtcDateString(dateStr) {
  let match = dateStr.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(\.\d{1,3})?/);
  if (match) {
    let ms = match[2] ? match[2] : '.000';
    return match[1] + ms + 'Z';
  }
  return dateStr;
}

const instructions = [
  "1. Select the Flow Meter number (1, 2, or 3)",
  "2. Flush Weed-It for a few seconds, then stop",
  "3. Enter the Goal in gallons (bucket size)",
  "4. Wait for the Start button to be enabled",
  "5. Press Start and begin filling the bucket",
  "6. Once the bucket is filled to the appropriate level, press Stop",
  "7. Wait for the flow level from the device to level off",
  "8. Press Save to store the results"
];

const FlowCalibration = () => {
  const [searchParams] = useSearchParams();
  const tractorId = searchParams.get('tractorId');
  const clientId = searchParams.get('clientId');

  const [tractor, setTractor] = useState(null);
  const [devices, setDevices] = useState([]);
  const [flowMeterNumber, setFlowMeterNumber] = useState('1');
  const [goal, setGoal] = useState('');
  const [status, setStatus] = useState(initialStatus);
  const [startingPulses, setStartingPulses] = useState('N/A');
  const [pulseCount, setPulseCount] = useState('N/A');
  const [pulsesPerGal, setPulsesPerGal] = useState('N/A');
  const [latestPulses, setLatestPulses] = useState(null);

  // Single state for calibration phase
  // idle, ready, calibrating, waitingForLevel, completed
  const [calibrationState, setCalibrationState] = useState('idle');

  // Fetch tractor details
  useEffect(() => {
    const fetchTractor = async () => {
      if (!clientId || !tractorId) return;

      try {
        const token = await authService.getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/all`,
          {
            headers: !token ? {} : { Authorization: `Bearer ${token}` }
          }
        );

        if (response.ok) {
          const tractors = await response.json();
          const foundTractor = tractors.find(t => t.id === parseInt(tractorId));
          setTractor(foundTractor);
        }
      } catch (error) {
        console.error("Error fetching tractor:", error);
      }
    };

    fetchTractor();
  }, [clientId, tractorId]);

  // Fetch devices for the tractor
  useEffect(() => {
    const fetchDevices = async () => {
      if (!tractorId) return;
      try {
        const token = await authService.getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/admin/devices/all-by-tractor?tractorId=${tractorId}`,
          {
            headers: !token ? {} : { Authorization: `Bearer ${token}` }
          }
        );
        if (response.ok) {
          const devicesData = await response.json();
          setDevices(devicesData);
        } else {
          setDevices([]);
        }
      } catch (error) {
        setDevices([]);
        console.error("Error fetching devices:", error);
      }
    };

    fetchDevices();
  }, [tractorId]);

  // Get the first device serial number
  const deviceSerialNumber = devices.length > 0 ? devices[0].serialNumber : null;

  // Helper to process heartbeats for each mode
  const processHeartbeats = (data) => {
    const now = Date.now();
    const recent = (Array.isArray(data) ? data : []).filter(hb => {
      if (!hb.eventDateUtc) return false;
      const ts = new Date(normalizeUtcDateString(hb.eventDateUtc)).getTime();
      return now - ts <= 90000;
    });
    const pulseProp = `cumulativeFlowPulseCount${flowMeterNumber}`;
    const pulses = recent.map(hb => hb[pulseProp]).filter(v => typeof v === 'number');
    const allHavePulse = recent.length >= heartBeatLookBackThreshold && recent.every(hb => hb[pulseProp] !== undefined);
    const uniquePulses = allHavePulse
      ? Array.from(new Set(recent.map(hb => hb[pulseProp])))
      : [];
    const goalVal = parseFloat(goal);
    const goalIsValid = !isNaN(goalVal) && goalVal > 0;

    switch (calibrationState) {
      case 'idle':
      case 'ready':
        if (allHavePulse && uniquePulses.length === 1 && goalIsValid) {
          setCalibrationState('ready');
          setStatus('Ready to start calibration');
          setStartingPulses(uniquePulses[0]);
        } else {
          setCalibrationState('idle');
          setStatus(initialStatus);
          setStartingPulses('N/A');
        }
        setPulseCount('N/A');
        setPulsesPerGal('N/A');
        break;
      case 'calibrating':
        if (pulses.length > 0) {
          const maxPulses = Math.max(...pulses);
          setLatestPulses(maxPulses);
          const pulseCountVal = maxPulses - Number(startingPulses);
          setPulseCount(pulseCountVal);

          if (goalIsValid) {
            setPulsesPerGal((pulseCountVal / goalVal).toFixed(2));
            setStatus('Calibrating...');
          } else {
            setPulsesPerGal('N/A');
            setStatus('Enter a valid goal to calculate Pulses / Gal');
          }
        } else {
          setStatus('Waiting for valid heartbeats...');
        }
        break;
      case 'waitingForLevel':
        if (pulses.length >= heartBeatLookBackThreshold) {
          const lastHbs = pulses.slice(-1 * heartBeatLookBackThreshold);
          const allEqual = lastHbs.every(v => v === lastHbs[0]);
          if (allEqual) {
            const levelOffValue = lastHbs[0];
            setLatestPulses(levelOffValue);
            const pulseCountVal = levelOffValue - Number(startingPulses);
            setPulseCount(pulseCountVal);

            const goalVal = parseFloat(goal);
            if (!isNaN(goalVal) && goalVal > 0) {
              setPulsesPerGal((pulseCountVal / goalVal).toFixed(2));
            } else {
              setPulsesPerGal('N/A');
            }

            setCalibrationState('completed');
            setStatus('Flow leveled off. Ready to save or reset.');
          }
        }
        break;
      default:
        setCalibrationState('completed');
        break;
    }
  };

  // Single polling effect
  useEffect(() => {
    let intervalId;
    if (deviceSerialNumber && flowMeterNumber) {
      intervalId = setInterval(async () => {
        try {
          const res = await fetch(
            `/api/admin/devices/recent-heartbeats?deviceSerialNumber=${encodeURIComponent(deviceSerialNumber)}`
          );
          if (res.ok) {
            const data = await res.json();
            processHeartbeats(data);
          }
        } catch {
          setStatus('Heartbeat fetch error');
        }
      }, 1000);
    }
    return () => clearInterval(intervalId);
    // eslint-disable-next-line
  }, [deviceSerialNumber, flowMeterNumber, calibrationState, goal, startingPulses]);

  // Handlers
  const handleStart = () => {
    setCalibrationState('calibrating');
    setPulseCount(0);
    setPulsesPerGal(0);
  };

  const handleStop = () => {
    setCalibrationState('waitingForLevel');
    setStatus('Waiting for flow to level off');
  };

  const handleSave = async () => {
    setStatus('Saving calibration...');
    try {
      // 1. GET the device by serial number
      const getRes = await fetch(
        `/api/admin/devices/by-serial-number?clientId=${encodeURIComponent(clientId)}&deviceSerialNumber=${encodeURIComponent(deviceSerialNumber)}`
      );
      if (!getRes.ok) {
        setStatus('Failed to fetch device for update.');
        return;
      }
      const device = await getRes.json();

      // Determine which flow meter property to update
      if (flowMeterNumber === '1') device.flowPulsesPerGal1 = Number(pulsesPerGal);
      if (flowMeterNumber === '2') device.flowPulsesPerGal2 = Number(pulsesPerGal);
      if (flowMeterNumber === '3') device.flowPulsesPerGal3 = Number(pulsesPerGal);

      // 3. PUT the device with the updated calibration value
      const putRes = await fetch(
        `/api/admin/devices/update`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(device)
        }
      );
      if (putRes.ok) {
        setStatus('Calibration saved successfully.');
        setCalibrationState('saved'); // <-- disables Save, enables Reset
      } else {
        setStatus('Failed to update calibration.');
      }
    } catch {
      setStatus('Error saving calibration.');
    }
  };

  const handleReset = () => {
    setGoal('');
    setStatus(initialStatus);
    setStartingPulses('N/A');
    setPulseCount('N/A');
    setPulsesPerGal('N/A');
    setLatestPulses(null);
    setCalibrationState('idle');
  };

  const handleFlowMeterChange = (event) => {
    setFlowMeterNumber(event.target.value);
    // Reset calibration when changing flow meter
    handleReset();
  };

  // Button enabled/disabled logic
  const startEnabled = calibrationState === 'ready';
  const stopEnabled = calibrationState === 'calibrating';
  const saveEnabled = calibrationState === 'completed';
  const resetEnabled = calibrationState === 'completed' || calibrationState === 'saved';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        pt: 2
      }}
    >
      <Paper elevation={3} sx={{ p: 4, minWidth: 400, maxWidth: 480, borderRadius: 4, mt: 2 }}>
        <Stack spacing={3}>
          <Typography variant="h4" sx={{ pb: 1 }}>
            {tractor ? `${tractor.name}` : ''}
          </Typography>
          <Typography variant="h5" sx={{ pb: 1 }}>
            Flow Meter Calibration
          </Typography>
          <Divider />
          {/* Instructions Section */}
          <Box sx={{ background: '#f0f4f8', borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
              Instructions
            </Typography>
            <Stack spacing={0.5}>
              {instructions.map((item, idx) => (
                <Typography variant="body2" key={idx} sx={{ pl: 1 }}>
                  {item}
                </Typography>
              ))}
            </Stack>
          </Box>
          <Divider />
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Typography variant="body2">
              <b>Serial Number:</b> {deviceSerialNumber || 'Loading...'}
            </Typography>
          </Stack>
          <FormControl sx={{ flex: 1 }}>
            <FormLabel>Flow Meter Number</FormLabel>
            <Select
              value={flowMeterNumber}
              onChange={handleFlowMeterChange}
              disabled={calibrationState === 'calibrating'}
            >
              <MenuItem value="1">Flow Meter 1</MenuItem>
              <MenuItem value="2">Flow Meter 2</MenuItem>
              <MenuItem value="3">Flow Meter 3</MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Goal (gal)</FormLabel>
            <TextField
              value={goal}
              onChange={e => setGoal(e.target.value)}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9.]*' }}
              disabled={calibrationState === 'calibrating'}
              sx={{ background: '#fff', borderRadius: 1 }}
            />
          </FormControl>
          <Box sx={{ background: '#f9fafb', borderRadius: 2, p: 2 }}>
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                <b>Status:</b> {status}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <b>Starting Pulses:</b> {startingPulses}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <b>Pulses Count:</b> {pulseCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <b>Pulses / gal:</b> {pulsesPerGal}
              </Typography>
            </Stack>
          </Box>
          {/* Button rows: use flex=1 to make them equal width and fill the row */}
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              onClick={handleStart}
              variant="outlined"
              title="Start"
              disabled={!startEnabled}
              sx={{ flex: 1 }}
            >
              Start
            </Button>
            <Button
              onClick={handleStop}
              variant="outlined"
              title="Stop"
              disabled={!stopEnabled}
              sx={{ flex: 1 }}
            >
              Stop
            </Button>
          </Stack>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              onClick={handleSave}
              variant="outlined"
              title="Save"
              disabled={!saveEnabled}
              sx={{ flex: 1 }}
            >
              Save
            </Button>
            <Button
              onClick={handleReset}
              variant="outlined"
              title="Reset"
              disabled={!resetEnabled}
              sx={{ flex: 1 }}
            >
              Reset
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

export default FlowCalibration;